import { defineNuxtPlugin } from 'nuxt/app';
import { watch } from 'vue';
import { requestIdleCallback } from '~/utils/requestIdleCallback';

/**
 * Compare/customer stores are not needed for first paint. Schedule init on
 * idle so they stay out of the eager entry parse; timeout keeps login merge
 * from waiting indefinitely.
 *
 * Auth must use `useShopAuth().isAuthenticated` (OIDC + Magento shop status),
 * not bare `loggedIn` — `onLoggedIn()` early-returns until shop is ready.
 */
export default defineNuxtPlugin(() => {
  requestIdleCallback(
    () => {
      void initCompareListAuth();
    },
    { timeout: 2000 }
  );
});

async function initCompareListAuth() {
  const [
    { useCompareListStore },
    { useCustomerAccountStore },
    { useCompareListCookie }
  ] = await Promise.all([
    import('~/stores/compareListStore/compareListStore'),
    import('~/stores/customerAccountStore/customerAccountStore'),
    import('~/composables/useCompareListCookie')
  ]);

  const compareListStore = useCompareListStore();
  const customerAccountStore = useCustomerAccountStore();
  const { isAuthenticated } = useShopAuth();
  const { $shopStore, $locale } = useNuxtApp();

  async function tryLoadGuestCompareList() {
    if (isAuthenticated.value) {
      return;
    }
    const cookie = useCompareListCookie($locale.value);
    const storeKey = $shopStore.value;
    if (!cookie.value[storeKey]) {
      return;
    }
    try {
      await compareListStore.refresh();
    } catch {
      cookie.value = { ...cookie.value, [storeKey]: undefined };
    }
  }

  // Handle state on initial page load (for OAuth redirects)
  if (isAuthenticated.value) {
    await customerAccountStore.loadCustomer();

    const guestUid = useCompareListCookie($locale.value).value[
      $shopStore.value
    ];
    const customerUid = customerAccountStore.customerData?.compare_list?.uid;

    if (guestUid && customerUid && guestUid !== customerUid) {
      await compareListStore.onLoggedIn();
    } else {
      await compareListStore.refresh();
    }
  }

  // Guests: reload after `$shopStore` resolves from PDP config (compare cookie is keyed by store).
  watch(
    () =>
      [
        isAuthenticated.value,
        $shopStore.value,
        useCompareListCookie($locale.value).value
      ] as const,
    () => {
      if (isAuthenticated.value) {
        return;
      }
      void tryLoadGuestCompareList();
    },
    { deep: true, immediate: true }
  );

  watch(isAuthenticated, async (isShopAuthenticated, wasShopAuthenticated) => {
    if (isShopAuthenticated && wasShopAuthenticated === false) {
      await compareListStore.onLoggedIn();
    } else if (wasShopAuthenticated === true && !isShopAuthenticated) {
      compareListStore.onLoggedOut();
    }
  });
}
